import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
  TextField,
  Button,
} from '@mui/material';
import {
  stableSort,
  getComparator,
} from "../../../../../common-utilities/sortingUtils";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GetTestQualityControlTypes, DeleteTestQualityControlType } from '../../../../services/TestLibService';
import CommonLoader from '../../../common/CommonLoader';
import ViewQualityControlType from './ViewQualityControlType';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteConfirmation from '../../../common/DeleteConfirmation';
import { NotificationManager } from 'react-notifications';
import AddTestQualityControlTypeDialog from './AddTestQualityControlTypeDialog'; // Import the AddTestQualityControlTypeDialog component

const TestQualityControlTypesTable = () => {
  const columns = ['QC Type', 'Description', 'Formula ID', 'Actions'];
  const columnHeaders = ['qcType', 'description', 'formulaId'];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [qcTypesData, setQcTypesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [selectedQcType, setSelectedQcType] = useState(null);
  const [selectedQcTypeToDelete, setSelectedQcTypeToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isAddQcTypeDialogOpen, setIsAddQcTypeDialogOpen] = useState(false);

  useEffect(() => {
    fetchQcTypes();
  }, [deleted]);

  const fetchQcTypes = async () => {
    try {
      setLoading(true);
      const response = await GetTestQualityControlTypes();
      setQcTypesData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error('Error fetching QC types:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filteredResults = qcTypesData.filter((qcType) =>
      columnHeaders.some((header) =>
        qcType[header]?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, qcTypesData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (qcType) => {
    setSelectedQcType(qcType);
  };

  const handleBackClick = () => {
    setSelectedQcType(null);
    setSearchTerm('');
  };

  const handleDeleteClick = (qcType) => {
    setSelectedQcTypeToDelete(qcType.qcType);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteTestQualityControlType(selectedQcTypeToDelete);
      if (response.results === true) {
        NotificationManager.success('Test quality control type deleted successfully');
        setIsDeleteConfirmationOpen(false);
        setDeleted(!deleted);
      } else {
        NotificationManager.error('Error deleting test quality control type');
      }
    } catch (error) {
      NotificationManager.error('Error deleting test quality control type');
    }
  };

  const handleAddQcTypeClick = () => {
    setSelectedQcType(null);
    setIsAddQcTypeDialogOpen(true);
  };

  const handleEditQcTypeClick = (qcType) => {
    setSelectedQcType(qcType);
    setIsAddQcTypeDialogOpen(true);
  };

  const handleAddQcTypeDialogClose = () => {
    setIsAddQcTypeDialogOpen(false);
    setSelectedQcType(null);
  };

  const handleAddQcTypeDialogSuccess = () => {
    setIsAddQcTypeDialogOpen(false);
    setSelectedQcType(null);
    setDeleted(!deleted);
  };

  const renderActions = (qcType) => (
    <div style={{ minWidth: '120px' }}>
      <IconButton
        aria-label="edit"
        style={{ color: 'orange' }}
        onClick={() => handleEditQcTypeClick(qcType)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: 'green' }}
        onClick={() => handleViewClick(qcType)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: 'red' }}
        onClick={() => handleDeleteClick(qcType)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">Test Quality Control Types Management </span>
    <hr />
      {selectedQcType && !isAddQcTypeDialogOpen? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: '16px', color: 'green' }}
          >
            Back
          </Button>
          <ViewQualityControlType qcType={selectedQcType} />
        </>
      ) : (
        <>
          <div style={{ display: 'flex', marginBottom: '16px', alignItems: 'center' }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: '16px', width: '80%' }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddQcTypeClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: '20px' }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main, color: 'white' }}>
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableCell
                        key={column}
                        onClick={() => columnHeaders[index] && handleRequestSort(columnHeaders[index])}
                        sx={{ cursor: columnHeaders[index] ? 'pointer' : 'default' }}
                      >
                        {columnHeaders[index] ? (
                          <TableSortLabel
                            active={orderBy === columnHeaders[index]}
                            direction={orderBy === columnHeaders[index] ? order : 'asc'}
                            sx={{
                              color: 'white !important',
                              '& .MuiTableSortLabel-icon': { color: 'white !important' },
                            }}
                          >
                            {column}
                          </TableSortLabel>
                        ) : (
                          column
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((qcType, index) => (
                    <TableRow
                      key={qcType.qcType}
                      sx={{ backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'white' }}
                    >
                      {columnHeaders.map((column) => (
                        <TableCell key={column}>{qcType[column]}</TableCell>
                      ))}
                      <TableCell>{renderActions(qcType)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message="Are you sure you want to delete this quality control type?"
            color="red"
          />
        </>
      )}

      {isAddQcTypeDialogOpen && (
        <AddTestQualityControlTypeDialog
          open={isAddQcTypeDialogOpen}
          onClose={handleAddQcTypeDialogClose}
          onSave={handleAddQcTypeDialogSuccess}
          selectedQcType={selectedQcType}
        />
      )}
    </>
  );
};

export default TestQualityControlTypesTable;
